import clsx from 'clsx';
import { useId } from 'react';

import styles from './liquid.module.css';

const params = {
    baseFrequencyX: 0.016,
    baseFrequencyY: 0.012,
    seed: 39,
    scale: 160,
    blurStdDeviation: 0,
    backdropBlur: 9,
    tintOpacity: 0.35,
    shineOpacity: 0.65,
};

/**
 * An SVG that represents a glass browser chrome window. Used to superimpose assets on top of, e.g. an illustration of a site skeleton or template.
 */
export function GlassBrowser(props: React.ComponentPropsWithoutRef<'svg'>) {
    const { style, className, ...svgProps } = props;
    const filterId = useId();
    const glassDistortionFilterId = useId();

    return (
        <>
            {/* Hidden SVG with glass distortion filter */}
            <svg style={{ display: 'none' }} role="presentation">
                <title>Glass Distortion Filter</title>
                <filter
                    id={glassDistortionFilterId}
                    x="-10%"
                    y="-10%"
                    width="120%"
                    height="120%"
                    filterUnits="objectBoundingBox"
                >
                    <feTurbulence
                        type="fractalNoise"
                        baseFrequency={`${params.baseFrequencyX} ${params.baseFrequencyY}`}
                        numOctaves={1}
                        seed={params.seed}
                        result="turbulence"
                    />
                    <feGaussianBlur
                        in="turbulence"
                        stdDeviation={params.blurStdDeviation}
                        result="softMap"
                    />
                    <feDisplacementMap
                        in="SourceGraphic"
                        in2="softMap"
                        scale={params.scale}
                        xChannelSelector="R"
                        yChannelSelector="G"
                    />
                </filter>
            </svg>

            <div
                className={clsx(
                    styles.liquidGlassWrapper,
                    'absolute inset-0 flex overflow-hidden rounded-2xl shadow-elevated dark:shadow-subtle'
                )}
                style={{
                    ['--glass-distortion-filter' as string]: `url(#${glassDistortionFilterId})`,
                    ['--backdrop-blur' as string]: `${params.backdropBlur}px`,
                    ['--tint-opacity' as string]: params.tintOpacity.toString(),
                    ['--shine-opacity' as string]: params.shineOpacity.toString(),
                    width: '1101px',
                    height: '644px',
                    ...style,
                }}
            >
                <svg
                    {...svgProps}
                    width="1101"
                    height="644"
                    viewBox="0 0 1101 644"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                        position: 'relative',
                        zIndex: 1,
                        borderRadius: '16px',
                    }}
                >
                    <title>Browser Chrome Window</title>
                    <g filter={`url(#${filterId})`}>
                        {/* Address bar skeleton */}
                        <rect
                            x="401.075"
                            y="21.8262"
                            width="286.851"
                            height="19.3327"
                            rx="9.66636"
                            fill="var(--sp-color-bg-inverted)"
                            fillOpacity="0.2"
                        />
                    </g>
                    <defs>
                        <filter
                            id={filterId}
                            x="0"
                            y="0"
                            width="1101"
                            height="644"
                            filterUnits="userSpaceOnUse"
                            colorInterpolationFilters="sRGB"
                        >
                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                            <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                            />
                            <feMorphology
                                radius="3"
                                operator="dilate"
                                in="SourceAlpha"
                                result="effect1_dropShadow_98_6619"
                            />
                            <feOffset dy="8" />
                            <feGaussianBlur stdDeviation="7" />
                            <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
                            />
                            <feBlend
                                mode="normal"
                                in2="BackgroundImageFix"
                                result="effect1_dropShadow_98_6619"
                            />
                            <feColorMatrix
                                in="SourceAlpha"
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                result="hardAlpha"
                            />
                            <feOffset dy="2" />
                            <feGaussianBlur stdDeviation="1" />
                            <feComposite in2="hardAlpha" operator="out" />
                            <feColorMatrix
                                type="matrix"
                                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
                            />
                            <feBlend
                                mode="normal"
                                in2="effect1_dropShadow_98_6619"
                                result="effect2_dropShadow_98_6619"
                            />
                            <feBlend
                                mode="normal"
                                in="BackgroundImageFix"
                                in2="effect2_dropShadow_98_6619"
                                result="BackgroundImageFix"
                            />
                            <feBlend
                                mode="normal"
                                in="SourceGraphic"
                                in2="BackgroundImageFix"
                                result="shape"
                            />
                        </filter>
                    </defs>
                </svg>
            </div>
        </>
    );
}
